import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import style from "../common.module.scss";
import { File, Order } from "../../../../services";
import CustomButton from "../../../custom/custom-button/CustomButton";
import { toast } from "react-toastify";
import { rejectstatus } from "../../../../mock";
import OrderDocumentUpload from "./OrderDocumentUpload";
import ConfirmModal from "../../../shared/ConfirmModal/ConfirmModal";
import { useSelector } from "react-redux";

const OrderDecision = ({ item, onUpdateData, fetchOrderData }) => {
  const { t } = useTranslation();
  const company = useSelector((state) => state.user.company);

  const [isLoading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLoadingWarningModal, setShowLoadingWarningModal] = useState(false);
  const [showUnloadingWarningModal, setShowUnloadingWarningModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [documents, setDocuments] = useState({
    1: null, // Фото груза
    2: null, // Фото пломбы при погрузке
    3: null, // ТТН от Водителя
    4: null, // Фото пломбы при выгрузке
  });
  const [documentErrors, setDocumentErrors] = useState({
    1: "",
    2: "",
    3: "",
    4: "",
  });

  const openConfirmModal = (action) => {
    setPendingAction(() => action);
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (pendingAction) {
      await pendingAction();
      setPendingAction(null);
    }
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const updateStatus = async (status, successMessage, addressId = null) => {
    setLoading(true);
    try {
      await Order.updateExpeditorStatus(item.id, status, addressId);
      toast.success(successMessage);
      fetchOrderData();
      onUpdateData();
    } catch (error) {
      console.error("Ошибка при отправке status:", error);
      toast.error(t("orderInfo.genericError"));
    } finally {
      setLoading(false);
    }
  };

  const handleDecisionOrder = (decision) => {
    openConfirmModal(() => {
      const successMessage =
        decision === "assignedexecutor"
          ? t("orderInfo.orderDecision.acceptOrderSuccess")
          : t("orderInfo.orderDecision.rejectOrderSuccess");
      updateStatus(decision, successMessage, "");
    });
  };

  const handleStatusUpdate = async (status, successMessage) => {
    if (item.status === "processloadingcargo" && !areDocumentsUploaded()) {
      setShowLoadingWarningModal(true);
      return;
    }

    if (item.status === "processunloadingcargo" && !areDocumentsUploaded()) {
      setShowUnloadingWarningModal(true);
      return;
    }

    openConfirmModal(async () => {
      if (item.status === "processloadingcargo") {
        const uploadSuccess = await uploadDocumentsToServer([1, 2]);
        if (!uploadSuccess) {
          return;
        }
      }

      if (item.status === "processunloadingcargo") {
        const uploadSuccess = await uploadDocumentsToServer([3, 4]);
        if (!uploadSuccess) {
          return;
        }

        const currentAddress = getCurrentAddress();
        const uncompletedAddresses = item?.addressList?.filter((address) => !address.isCompleted) || [];
        const uncompletedCount = uncompletedAddresses.length;

        if (uncompletedCount > 1) {
          updateStatus("onthetrip", t("orderInfo.orderDecision.onTheTripSuccess"), currentAddress?.id);
          return;
        }

        const finalStatus =
          item?.type === 1 && item?.companyId === company?.id ? "completed" : "completedbyexecutor";

        status = finalStatus;
      }

      const currentAddress = getCurrentAddress();
      const addressId =
        item.status === "processloadingcargo" || item.status === "processunloadingcargo"
          ? currentAddress?.id
          : "";

      updateStatus(status, successMessage, addressId);
    });
  };

  const updateStatusUnloading = () => {
    if (item.status === "processunloadingcargo" && !areDocumentsUploaded()) {
      setShowUnloadingWarningModal(true);
      return;
    }

    openConfirmModal(() => {
      const status =
        item?.type === 1 && item?.companyId === company?.id ? "completed" : "completedbyexecutor";
      updateStatus(status, t("orderInfo.orderDecision.acceptUnloadingSuccess"), "");
    });
  };

  const rejectConfirm = () => {
    openConfirmModal(() => {
      const status = rejectstatus(item.status);
      updateStatus(status, t("orderInfo.orderDecision.rejectUnloadingModerationSuccess"), "");
    });
  };

  const confirmLoadingWithoutDocuments = async () => {
    setShowLoadingWarningModal(false);

    // Проверяем и загружаем доступные документы
    const hasAnyDocuments = documents[1] || documents[2];
    if (hasAnyDocuments) {
      const uploadSuccess = await uploadDocumentsToServer([1, 2]);
      if (!uploadSuccess) {
        return;
      }
    }

    const currentAddress = getCurrentAddress();
    const addressId = currentAddress?.id || "";
    await updateStatus("onthetrip", t("orderInfo.orderDecision.onTheTripSuccess"), addressId);
  };

  const cancelLoadingWithoutDocuments = () => {
    setShowLoadingWarningModal(false);
  };

  const confirmUnloadingWithoutDocuments = async () => {
    setShowUnloadingWarningModal(false);

    // Проверяем и загружаем доступные документы
    const hasAnyDocuments = documents[3] || documents[4];
    if (hasAnyDocuments) {
      const uploadSuccess = await uploadDocumentsToServer([3, 4]);
      if (!uploadSuccess) {
        return;
      }
    }

    const currentAddress = getCurrentAddress();
    const uncompletedAddresses = item?.addressList?.filter((address) => !address.isCompleted) || [];
    const uncompletedCount = uncompletedAddresses.length;

    let status = item?.type === 1 && item?.companyId === company?.id ? "completed" : "completedbyexecutor";
    if (uncompletedCount > 1) {
      status = "onthetrip";
    }

    status === "onthetrip"
        ? t("orderInfo.orderDecision.onTheTripSuccess")
        : t("orderInfo.orderDecision.completeUnloadingSuccess");

    const addressId = currentAddress?.id || "";
    await updateStatus(
      status,
      status === "onthetrip"
        ? t("orderInfo.orderDecision.onTheTripSuccess")
        : t("orderInfo.orderDecision.completeUnloadingSuccess"),
      addressId,
    );
  };

  const cancelUnloadingWithoutDocuments = () => {
    setShowUnloadingWarningModal(false);
  };

  const handleUploadFile = (file, type) => {
    setDocuments((prev) => ({
      ...prev,
      [type]: file,
    }));
    setDocumentErrors((prev) => ({
      ...prev,
      [type]: "",
    }));
  };

  const uploadDocumentsToServer = async (documentTypes) => {
    try {
      const currentAddress = getCurrentAddress();

      if (!currentAddress) {
        toast.error(t("orderInfo.genericError"));
        return false;
      }

      for (const type of documentTypes) {
        const file = documents[type];
        if (!file) continue;

        const formData = new FormData();
        formData.append("type", String(type));
        formData.append("orderId", item.id);
        formData.append("addressId", currentAddress.id);
        formData.append("file", file);
        formData.append("FileName", file.name);
        formData.append("name", file.name);
        formData.append("Name", file.name);

        await File.addOrderDoc(formData);
      }

      toast.success(t("orderInfo.orderDecision.documentUploadSuccess"));
      return true;
    } catch (error) {
      console.error("Ошибка при загрузке документов:", error);
      toast.error(t("orderInfo.orderDecision.documentUploadError"));
      return false;
    }
  };

  const handleDeleteFile = (type) => {
    setDocuments((prev) => ({
      ...prev,
      [type]: null,
    }));
  };

  const getCurrentAddress = () => {
    return item?.addressList?.find((address) => !address.isCompleted);
  };

  const areDocumentsUploaded = () => {
    if (item.status === "processloadingcargo") {
      return documents[1] && documents[2];
    }
    if (item.status === "processunloadingcargo") {
      return documents[3] && documents[4];
    }
    return true;
  };

  useEffect(() => {
    setDocuments({
      1: null,
      2: null,
      3: null,
      4: null,
    });
    setDocumentErrors({
      1: "",
      2: "",
      3: "",
      4: "",
    });
  }, [item.id]);

  const renderButtonGroup = (buttons) => (
    <div style={{ display: "flex", gap: "16px", width: "100%" }}>
      {buttons.map((button, index) => (
        <CustomButton
          key={index}
          text={button.text}
          style={{ flex: "1" }}
          onClick={button.onClick}
          isLoading={isLoading}
          isWhite={button.isWhite}
          disabled={button.disabled || isLoading}
        />
      ))}
    </div>
  );

  const renderDecisionBlock = (title, buttons, documentsBlock) => (
    <div className={cn(style.cargo_wrapper)}>
      <div className={cn(style.cargo_title, style.orders_title)}>{title}</div>
      {documentsBlock && <div className={style.status_update}>{documentsBlock}</div>}
      {renderButtonGroup(buttons)}
    </div>
  );

  const renderStatus = () => {
    const { status } = item;

    const statusConfig = {
      assigned: {
        title: t("orderInfo.orderDecision.acceptRejectTitle"),
        buttons: [
          {
            text: t("orderInfo.orderDecision.acceptButton"),
            onClick: () => handleDecisionOrder("assignedexecutor"),
          },
          {
            text: t("orderInfo.orderDecision.rejectButton"),
            onClick: () => handleDecisionOrder("expeditorrejected"),
            isWhite: true,
          },
        ],
      },
      confirmed: {
        title: t("orderInfo.orderDecision.startLoadingTitle"),
        buttons: [
          {
            text: t("orderInfo.orderDecision.startLoadingButton"),
            onClick: () =>
              handleStatusUpdate("processloadingcargo", t("orderInfo.orderDecision.startLoadingSuccess")),
          },
        ],
      },
      headingtoloading: {
        title: t("orderInfo.orderDecision.startLoadingTitle"),
        buttons: [
          {
            text: t("orderInfo.orderDecision.startLoadingButton"),
            onClick: () =>
              handleStatusUpdate("processloadingcargo", t("orderInfo.orderDecision.startLoadingSuccess")),
          },
        ],
      },
      processloadingcargo: {
        title: t("orderInfo.orderDecision.completeLoadingTitle"),
        documents: (
          <>
            <OrderDocumentUpload
              type={1}
              title={t("orderInfo.listDocsOrder.docTypeCargoPhoto")}
              file={documents[1]}
              onUploadFile={handleUploadFile}
              onDeleteFile={handleDeleteFile}
              errorMessage={documentErrors[1]}
              isRequired={false}
            />
            <OrderDocumentUpload
              type={2}
              title={t("orderInfo.listDocsOrder.docTypeSealLoading")}
              file={documents[2]}
              onUploadFile={handleUploadFile}
              onDeleteFile={handleDeleteFile}
              errorMessage={documentErrors[2]}
              isRequired={false}
            />
          </>
        ),
        buttons: [
          {
            text: t("orderInfo.orderDecision.onTheTripButton"),
            onClick: () => handleStatusUpdate("onthetrip", t("orderInfo.orderDecision.onTheTripSuccess")),
          },
        ],
      },
      onthetrip: (() => {
        const currentAddress = getCurrentAddress();
        const isLoading = currentAddress?.type === "Loading";
        
        return {
          title: isLoading 
            ? t("orderInfo.orderDecision.startLoadingTitle")
            : t("orderInfo.orderDecision.startUnloadingTitle"),
          buttons: [
            {
              text: isLoading
                ? t("orderInfo.orderDecision.startLoadingButton")
                : t("orderInfo.orderDecision.startUnloadingButton"),
              onClick: () => {
                const nextStatus = isLoading ? "processloadingcargo" : "processunloadingcargo";
                const successMessage = isLoading 
                  ? t("orderInfo.orderDecision.startLoadingSuccess")
                  : t("orderInfo.orderDecision.startUnloadingSuccess");
                handleStatusUpdate(nextStatus, successMessage);
              },
            },
          ],
        };
      })(),
      processunloadingcargo: {
        title: t("orderInfo.orderDecision.completeUnloadingTitle"),
        documents: (
          <>
            <OrderDocumentUpload
              type={3}
              title={t("orderInfo.listDocsOrder.docTypeWaybill")}
              file={documents[3]}
              onUploadFile={handleUploadFile}
              onDeleteFile={handleDeleteFile}
              errorMessage={documentErrors[3]}
              isRequired={false}
            />
            <OrderDocumentUpload
              type={4}
              title={t("orderInfo.listDocsOrder.docTypeSealUnloading")}
              file={documents[4]}
              onUploadFile={handleUploadFile}
              onDeleteFile={handleDeleteFile}
              errorMessage={documentErrors[4]}
              isRequired={false}
            />
          </>
        ),
        buttons: [
          {
            text: t("orderInfo.orderDecision.completeUnloadingButton"),
            onClick: () => handleStatusUpdate("", t("orderInfo.orderDecision.startUnloadingSuccess")),
          },
        ],
      },
      unloadingcargomoderation: {
        title: `${t("orderInfo.orderDecision.unloadingModerationTitle")} ТК`,
        buttons: [
          {
            text: t("orderInfo.orderDecision.confirmButton"),
            onClick: updateStatusUnloading,
          },
          {
            text: t("orderInfo.orderDecision.rejectButton"),
            onClick: rejectConfirm,
            isWhite: true,
          },
        ],
      },
      completedbyexecutor: {
        title: `${t("orderInfo.orderDecision.unloadingModerationTitle")} ТК`,
        buttons: [
          {
            text: t("orderInfo.orderDecision.confirmButton"),
            onClick: updateStatusUnloading,
          },
          {
            text: t("orderInfo.orderDecision.rejectButton"),
            onClick: rejectConfirm,
            isWhite: true,
          },
        ],
      },
    };
    const config = statusConfig[status];
    if (!config) return null;

    return renderDecisionBlock(config.title, config.buttons, config.documents);
  };

  return (
    <>
      {renderStatus()}
      <ConfirmModal
        isOpen={showConfirmModal}
        title={t("orderInfo.orderDecisionAviation.confirmStatusChange")}
        message={t("orderInfo.orderDecisionAviation.areYouSureToProceed")}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
      <ConfirmModal
        isOpen={showLoadingWarningModal}
        title={t("orderInfo.orderDecision.loadingWithoutDocsWarningTitle")}
        message={t("orderInfo.orderDecision.loadingWithoutDocsWarningMessage")}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        onConfirm={confirmLoadingWithoutDocuments}
        onCancel={cancelLoadingWithoutDocuments}
        isLoading={isLoading}
      />
      <ConfirmModal
        isOpen={showUnloadingWarningModal}
        title={t("orderInfo.orderDecision.unloadingWithoutDocsWarningTitle")}
        message={t("orderInfo.orderDecision.unloadingWithoutDocsWarningMessage")}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        onConfirm={confirmUnloadingWithoutDocuments}
        onCancel={cancelUnloadingWithoutDocuments}
        isLoading={isLoading}
      />
    </>
  );
};

export default OrderDecision;
